import React, { useEffect, useMemo, useState } from "react";
import ReactModal from "react-modal";
import "../popups.css";
import { getApi } from "../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../Utils/headOfLineRole";
import {
  getFamilyTreeHeadLineRestriction,
  TREE_SIDE_API_FATHER,
  TREE_SIDE_API_MOTHER,
} from "../Utils/subscriptionAccess";
import { getImportTreeSideStateFromHeaderPreference } from "../Utils/importTreeSideLock";
import { isDashboardInLawMember } from "../Utils/dashboardMemberDisplayName";
import { compareDashboardMembersByOldest } from "../Utils/dashboardFamilyLineNumbers";
import FamilyLineSideHeadDropdown from "./FamilyLineSideHeadDropdown";
import { renderColorDotAndName } from "../Utils/colors";

if (process.env.NODE_ENV !== "test") {
  ReactModal.setAppElement("#root");
}

const hardcodedMembers = [
  { id: 1, name: "Richard Smith", familyLine: "Robert Smith (Red)", color: "#FE0000", generation: "Gen 6 – Great Grandchildren" },
  { id: 2, name: "Laura Smith", familyLine: "Robert Smith (Red)", color: "#FE0000", generation: "Gen 6 – Great Grandchildren" },
  { id: 3, name: "Daniel Smith", familyLine: "Charles Smith (Green)", color: "#3C7D22", generation: "Gen 6 – Great Grandchildren" },
  { id: 4, name: "Emma Smith", familyLine: "Charles Smith (Green)", color: "#3C7D22", generation: "Gen 6 – Great Grandchildren" },
  { id: 5, name: "Oliver Smith", familyLine: "Elizabeth Smith (Purple)", color: "#A020F0", generation: "Gen 6 – Great Grandchildren" },
  { id: 6, name: "Ava Smith", familyLine: "Elizabeth Smith (Purple)", color: "#A020F0", generation: "Gen 7 – Great* Grandchildren" },
];

const InviteMemberModal = ({ isOpen, onClose, onInvite }) => {
  const [search, setSearch] = useState("");
  const [genFilter, setGenFilter] = useState("All");
  const [lineFilter, setLineFilter] = useState("All");
  const [selectedId, setSelectedId] = useState(null);
  const [membersList, setMembersList] = useState([]);
  const [familyTree, setFamilyTree] = useState();
  const [headLineRestriction, setHeadLineRestriction] = useState(() =>
    getFamilyTreeHeadLineRestriction()
  );
  /** API `tree_side`: "Mother Side" | "Father Side". */
  const [importTreeSide, setImportTreeSide] = useState("");
  /** Locked when plan is single-line, or header family-side preference is set. */
  const [importSideSelectionLocked, setImportSideSelectionLocked] =
    useState(false);
  /** Which lines appear in the dropdown: both, or only mother/father when header fixes the side. */
  const [importSideVisibleSides, setImportSideVisibleSides] = useState("both");
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Column sort for the member table; null key keeps the API order (generation, then oldest). */
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  // Read family_tree_id once from localStorage (matches usage in familyTree.jsx)
  const familyTreeIdFromStorage = useMemo(() => getFamilyTreeIdFromStorage(), []);

  useEffect(() => {
    const sync = () => setHeadLineRestriction(getFamilyTreeHeadLineRestriction());
    sync();
    window.addEventListener("loggedInUserRoleUpdated", sync);
    const onStorage = (e) => {
      if (e.key === "loggedIn_user_role" || e.key == null) sync();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("loggedInUserRoleUpdated", sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const r = getFamilyTreeHeadLineRestriction();
    setHeadLineRestriction(r);

    if (r === "mother_only") {
      setImportTreeSide(TREE_SIDE_API_MOTHER);
      setImportSideSelectionLocked(true);
      setImportSideVisibleSides("both");
      return;
    }
    if (r === "father_only") {
      setImportTreeSide(TREE_SIDE_API_FATHER);
      setImportSideSelectionLocked(true);
      setImportSideVisibleSides("both");
      return;
    }

    setImportTreeSide("");
    setImportSideSelectionLocked(false);
    setImportSideVisibleSides("both");

    const { apiSide, locked, visibleSides } =
      getImportTreeSideStateFromHeaderPreference();
    if (locked && apiSide) {
      setImportTreeSide(apiSide);
      setImportSideSelectionLocked(true);
      setImportSideVisibleSides(visibleSides);
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    return hardcodedMembers.filter((m) => {
      const matchesSearch =
        !search ||
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.familyLine.toLowerCase().includes(search.toLowerCase()) ||
        m.generation.toLowerCase().includes(search.toLowerCase());
      const matchesGen = genFilter === "All" || m.generation.startsWith(genFilter);
      const matchesLine = lineFilter === "All" || m.familyLine.toLowerCase().includes(lineFilter.toLowerCase());
      return matchesSearch && matchesGen && matchesLine;
    });
  }, [search, genFilter, lineFilter]);

  const invitedList = useMemo(
    () => hardcodedMembers.filter((m) => m.id === selectedId),
    [selectedId]
  );

  const fetchMemebers = async () => {
    try {
      const treeId = familyTreeIdFromStorage || familyTree?.id || null;
      const response = await getApi("/family-tree-user-ids", {
        params: treeId ? { family_tree_id: treeId } : {},
      });
      console.log("response : ", response)
      const sortedMembersList = (response?.data?.familyMembers || [])
        .filter((m) => !isDashboardInLawMember(m))
        .slice()
        .sort((a, b) => {
          const genA = Number(a?.generation_level);
          const genB = Number(b?.generation_level);
          const levelA = Number.isFinite(genA) ? genA : Infinity;
          const levelB = Number.isFinite(genB) ? genB : Infinity;

          if (levelA !== levelB) return levelA - levelB;
          return compareDashboardMembersByOldest(a, b);
        });

      setMembersList(sortedMembersList);
      // setMembersList(response?.data?.familyMembers);
      setFamilyTree(response?.data?.familyTree);
      // Reset selectedId when members are fetched from API
      setSelectedId(null);
    } catch (error) {
      console.log("error : ", error)
    }
  }

  const sortedMembersList = useMemo(() => {
    const list = membersList || [];
    if (!sortConfig.key) return list;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    const nameOf = (m) =>
      `${m?.first_name || ""} ${m?.last_name || ""}`.trim() || m?.name || "";
    const colorOf = (m) => m?.color_name || m?.family_line_color || "";
    const byName = (a, b) =>
      nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: "base" });
    return list.slice().sort((a, b) => {
      if (sortConfig.key === "name") return dir * byName(a, b);
      if (sortConfig.key === "color") {
        // Colour groups follow the chosen direction; names stay A–Z inside each colour.
        const c = colorOf(a).localeCompare(colorOf(b), undefined, { sensitivity: "base" });
        return c !== 0 ? dir * c : byName(a, b);
      }
      const genA = Number(a?.generation_level);
      const genB = Number(b?.generation_level);
      const levelA = Number.isFinite(genA) ? genA : Infinity;
      const levelB = Number.isFinite(genB) ? genB : Infinity;
      // Ties keep the API order (oldest first) because Array#sort is stable.
      return levelA === levelB ? 0 : dir * (levelA - levelB);
    });
  }, [membersList, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const renderSortableTh = (key, label) => {
    const active = sortConfig.key === key;
    return (
      <th
        aria-sort={active ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
      >
        <button
          type="button"
          className={`family-member-picker-sort-btn${active ? " is-active" : ""}`}
          onClick={() => handleSort(key)}
        >
          {label}
          <span className="family-member-picker-sort-icon" aria-hidden="true">
            {active ? (sortConfig.direction === "asc" ? "▲" : "▼") : "↕"}
          </span>
        </button>
      </th>
    );
  };

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
      setSortConfig({ key: null, direction: "asc" });
      fetchMemebers();
      setSelectedId(null); // Reset selection when modal opens
    }
  }, [isOpen])

  useEffect(() => {
    console.log("selectedId : ", selectedId)
  }, [selectedId])

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="popup-card large invite-card"
      overlayClassName="popup-overlay invite-overlay"
      closeTimeoutMS={200}
    >
      {/* Header */}
      <div className="popup-header invite-header">
        <h2>{membersList?.length === 0 ? "Import Start/Top of the Line" : "Choose Family member"}</h2>

        <button className="close-btn" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {/* Body */}
      <div className="invite-grid">

        <div className="invite-right" style={{ width: "100%", marginBottom: "12px" }}>
          <FamilyLineSideHeadDropdown
            value={importTreeSide}
            onChange={setImportTreeSide}
            headLineRestriction={headLineRestriction}
            selectionLocked={importSideSelectionLocked}
            visibleSides={importSideVisibleSides}
            label={
              membersList?.length > 0
                ? "Select Head of the Line to Start Importing the Entire Line"
                : "Select Names for Top of the Line"
            }
            helperText={
              membersList?.length > 0
                ? "Heads of Line are the children of the Start/Top of the Line, Start/Top of the Line are the Grandparents"
                : ""
            }
            id="inviteImportFamilyLineSideHead"
            listboxId="invite-import-family-line-side-head-listbox"
          />
        </div>

        {/* Right: List and controls */}
        <div className="invite-right">
          {/* Controls */}
          <div className="invite-controls">
         
          </div>

          {/* <button className="btn submit-btn" onClick={() => (onInvite ? onInvite(selectedId ? [selectedId] : []) : null)}>
              Invite
            </button> */}
        </div>


        <div className="customer-tablle-main family-choose-member">
          <table className="family-member-picker-table">
            <colgroup>
              <col className="family-member-picker-col-radio" />
              <col className="family-member-picker-col-name" />
              <col className="family-member-picker-col-color" />
              <col className="family-member-picker-col-generation" />
              <col className="family-member-picker-col-spacer" />
            </colgroup>
            <thead>
              <tr>
                <th aria-hidden="true" />
                {renderSortableTh("name", "Name")}
                {renderSortableTh("color", "Family Color")}
                {renderSortableTh("generation", "Generation Level")}
                <th aria-hidden="true" className="family-member-picker-spacer-cell" />
              </tr>
            </thead>
            <tbody>

              {membersList?.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: '#6c757d' }}>
                    No members found
                  </td>
                </tr>
              ) : (
                sortedMembersList.map((m) => (
                  <tr key={`${m.id}`}>
                    <td>
                      <label className="custom-radio">
                        <input
                          type="radio"
                          name="root-member"
                          checked={selectedId === m.id}
                          onChange={() => setSelectedId(m.id)}
                        />
                        <span className="radio-mark"></span>
                      </label>
                    </td>
                    <td>
                      <h6 className="mb-0">
                        {m.first_name + " " + m.last_name || m.name || "Member"}
                      </h6>
                    </td>
                    <td>
                      {renderColorDotAndName(
                        m?.hex_code || m?.family_line_hex_code || "#000000",
                        m?.color_name || m?.family_line_color || ""
                      )}
                    </td>
                    <td>{m.generation_level}</td>
                    <td aria-hidden="true" className="family-member-picker-spacer-cell" />
                  </tr>
                ))
              )}
           
            </tbody>
          </table>
        </div>

      </div>

      {/* Footer */}
      <div className="popup-actions">
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button 
          className="btn submit-btn"
          disabled={
            isSubmitting ||
            (membersList?.length > 0 && !selectedId) ||
            (headLineRestriction === "both" && !importTreeSide)
          }
          style={{
            backgroundColor:
              isSubmitting ||
              (membersList?.length > 0 && !selectedId) ||
              (headLineRestriction === "both" && !importTreeSide)
                ? "#A6A6B5"
                : "#6366f1",
            cursor:
              isSubmitting ||
              (membersList?.length > 0 && !selectedId) ||
              (headLineRestriction === "both" && !importTreeSide)
                ? "not-allowed"
                : "pointer",
            opacity:
              isSubmitting ||
              (membersList?.length > 0 && !selectedId) ||
              (headLineRestriction === "both" && !importTreeSide)
                ? 0.6
                : 1,
          }}
          onClick={() => {
            if (isSubmitting) return;
            // Prevent action if button is disabled
            if (membersList?.length > 0 && !selectedId) {
              return;
            }
            if (headLineRestriction === "both" && !importTreeSide) {
              return;
            }

            const side =
              headLineRestriction === "mother_only"
                ? TREE_SIDE_API_MOTHER
                : headLineRestriction === "father_only"
                  ? TREE_SIDE_API_FATHER
                  : importTreeSide;

            const selectionLockedForPayload =
              importSideSelectionLocked ||
              headLineRestriction === "mother_only" ||
              headLineRestriction === "father_only";

            const payload =
              selectedId && membersList?.length > 0
                ? {
                    selectedId: selectedId,
                    // Prefer localStorage family_tree_id; fallback to API response
                    familyTreeId: familyTreeIdFromStorage || familyTree?.id || null,
                    tree_side: side,
                    tree_side_selection_locked: selectionLockedForPayload,
                    tree_side_visible_sides: importSideVisibleSides,
                  }
                : {
                    tree_side: side,
                    tree_side_selection_locked: selectionLockedForPayload,
                    tree_side_visible_sides: importSideVisibleSides,
                  };

            setIsSubmitting(true);
            onInvite ? onInvite(payload) : onClose();
          }}
        >
          {membersList?.length === 0 ? "Import your Family Data" : "Proceed to import"}
        </button>
      </div>
    </ReactModal>
  );
};

export default InviteMemberModal;